/* ============================================================
   Onde esta página está rodando

   Serve para EXPLICAR e para MEDIR, nunca para decidir o que a pessoa
   pode fazer. Quem decide se dá para comprar é `podeComprar()`, que
   pergunta à loja; user-agent se forja com uma linha no DevTools.

   A ordem dos sinais é da prova para o palpite, e é por isso que ela
   está escrita aqui em vez de espalhada em ifs:

     1. `document.referrer` começando com `android-app://` é PROVA de
        estar dentro do app da Play. É o mesmo laço que a verificação do
        `assetlinks.json` estabelece, e não sai de user-agent;
     2. `navigator.standalone` só existe no Safari, e diz que é PWA do
        iPhone instalado na tela de início;
     3. `userAgentData` é o que os navegadores modernos oferecem de
        propósito — mas só existe no Chromium;
     4. user-agent é o último recurso, e mente: os navegadores
        congelaram a string, e o iPad se declara Mac desde o iPadOS 13.
        O desempate ali é `maxTouchPoints`.
   ============================================================ */

export type Sistema = "android" | "ios" | "computador" | "desconhecido";

/** Está dentro do app instalado pela Play Store? */
export const noAppDaPlay = (): boolean =>
  typeof document !== "undefined" && document.referrer.startsWith("android-app://");

/** Instalado como app (Play, tela de início do iPhone, ou PWA no desktop). */
export function instalado(): boolean {
  if (noAppDaPlay()) return true;
  if ((navigator as { standalone?: boolean }).standalone === true) return true;
  try {
    return matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

interface ComUAData {
  userAgentData?: { platform?: string; mobile?: boolean };
}

export function sistema(): Sistema {
  if (noAppDaPlay()) return "android";

  const ua = navigator.userAgent;
  const dados = (navigator as unknown as ComUAData).userAgentData;

  const plataforma = dados?.platform ?? "";
  if (plataforma) {
    if (plataforma === "Android") return "android";
    /* `userAgentData` não distingue iPhone de Mac — a Apple não
       implementa a API. Cai no user-agent abaixo. */
    if (plataforma === "Windows" || plataforma === "Linux" || plataforma === "Chrome OS") {
      return "computador";
    }
  }

  if (/android/i.test(ua)) return "android";

  /* iPad se declara "Macintosh" desde o iPadOS 13, e a única diferença
     que sobra é a tela ser de toque. Sem este desempate, todo iPad vira
     "computador" e recebe a mensagem errada. */
  const iOS = /iPhone|iPod/i.test(ua)
    || (/iPad/i.test(ua))
    || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  if (iOS) return "ios";

  if (/Macintosh|Windows|Linux|CrOS/i.test(ua)) return "computador";
  return "desconhecido";
}
